import React, { forwardRef, useImperativeHandle, useRef, useState, useEffect, useCallback } from 'react';
import ReactPlayer from 'react-player';
import { Box, CircularProgress, Alert, Typography } from '@mui/material';
import { googleDriveService } from '../services/googleDriveService';
import { incrementAttention, getDb, waitForFirebase } from '../services/firebase';
import { doc, updateDoc, increment, getDoc, arrayUnion, serverTimestamp } from 'firebase/firestore';
import { getAuth } from '../services/firebase';
import { useWatchProgress } from '../hooks/useWatchProgress';
import WatchProgressDialog from './WatchProgressDialog';

interface VideoPlayerProps {
  url: string;
  playing: boolean;
  volume: number;
  muted: boolean;
  playbackRate: number;
  initialStartTime?: number | null; // 🔧 新增：初始起始時間（用於 YouTube start 參數），單位為秒
  onProgress?: (state: any) => void;
  onDuration?: (duration: number) => void;
  onReady?: () => void;
  onError?: (error: any) => void;
  onPlay?: () => void; // 新增：播放事件回調
  onPause?: () => void; // 新增：暫停事件回調
  style?: React.CSSProperties;
  width?: string | number;
  height?: string | number;
  onTimeUpdate?: (currentTime: number) => void;
  onCaptionTextChange?: (text: string) => void;
  videoId?: string; // 新增：YouTube videoId
  currentTime?: number; // 新增：當前播放時間
  firestoreId?: string; // 新增：Firestore 文件 ID，用於更新 lastOpenDate
  videoTitle?: string; // 新增：影片標題
  onVerticalVideoChange?: (isVertical: boolean) => void; // 新增：影片方向變化回調
  disableWatchProgress?: boolean; // 新增：是否禁用觀看進度對話框
}

// 字幕介面
interface Caption {
  id: string;
  language: string;
  trackKind: string;
  isAutoSynced: boolean;
  isDefault: boolean;
  isCC: boolean;
  name: string;
}

const VideoPlayer = forwardRef<ReactPlayer, VideoPlayerProps>(
  ({ 
    url, 
    playing, 
    volume, 
    muted, 
    playbackRate, 
    initialStartTime = null, // 🔧 新增：初始起始時間
    onProgress, 
    onDuration, 
    onReady, 
    onError,
    onPlay, // 新增：播放事件回調
    onPause, // 新增：暫停事件回調
    style, 
    width = '100%', 
    height = '100%',
    onTimeUpdate,
    onCaptionTextChange,
    videoId,
    currentTime,
    firestoreId,
    videoTitle,
    onVerticalVideoChange,
    disableWatchProgress = false
  }, ref) => {
    const playerRef = useRef<ReactPlayer>(null);
    const [streamUrl, setStreamUrl] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    
    // 字幕相關狀態
    const [captions, setCaptions] = useState<Caption[]>([]);
    const [selectedCaption, setSelectedCaption] = useState<Caption | null>(null);
    const [showCaptionControls, setShowCaptionControls] = useState(false);
    
    // 新增：影片尺寸相關狀態
    const [videoDimensions, setVideoDimensions] = useState<{ width: number; height: number } | null>(null);
    const [isMobile, setIsMobile] = useState(false);
    const [isVerticalVideo, setIsVerticalVideo] = useState(false);
    
  // 觀看進度相關狀態
  const [showProgressDialog, setShowProgressDialog] = useState(false);
  const [videoDuration, setVideoDuration] = useState(0);
  const [hasShownDialog, setHasShownDialog] = useState(false);
    
    // 提取影片ID用於觀看進度管理
    const extractVideoId = useCallback((url: string): string => {
      if (!url) return '';
      
      // YouTube URL
      if (url.includes('youtube.com') || url.includes('youtu.be')) {
        const match = url.match(/(?:v=|youtu\.be\/|embed\/)([a-zA-Z0-9_-]{11})/);
        return match ? match[1] : '';
      }
      
      // 其他影片源，使用 URL 的 hash 作為 ID
      return url.split('/').pop() || url;
    }, []);
    
    const currentVideoId = videoId || extractVideoId(url);
    
  // 使用觀看進度 Hook
  const {
    currentProgress,
    hasProgress,
    saveProgress,
    restoreProgress,
    clearProgress,
    isRestoring,
    progressPercentage,
    formattedProgress,
    shouldShowDialog,
    closeDialog
  } = useWatchProgress({
    videoId: currentVideoId,
    videoUrl: url,
    autoSave: true,
    saveInterval: 10000,
    showProgressNotification: true,
    showProgressDialog: !disableWatchProgress // 🔧 新增：根據 disableWatchProgress 參數決定是否顯示對話框
  });

    // 將 ref 暴露給父組件
    useImperativeHandle(ref, () => playerRef.current!);
    
  // 觀看進度處理函數
  const handleRestoreProgress = useCallback(async (time: number) => {
    // 先調用 Hook 中的 restoreProgress 來正確更新狀態
    const restoredTime = await restoreProgress();
    
    if (playerRef.current && restoredTime !== null) {
      // 🔧 修復：延遲執行 seekTo，確保播放器已準備好
      setTimeout(() => {
        if (playerRef.current) {
          playerRef.current.seekTo(restoredTime, 'seconds'); // 第二個參數為 'seconds' 表示以秒為單位
          // 調試日誌已移除
        }
      }, 1000); // 延遲1秒，確保播放器完全載入
    }
    // 關閉對話框並重置狀態
    setShowProgressDialog(false);
    setHasShownDialog(false);
  }, [restoreProgress]);
    
  const handleStartFromBeginning = useCallback(() => {
    if (playerRef.current) {
      playerRef.current.seekTo(0);
      // 調試日誌已移除
    }
    // 關閉對話框並重置狀態
    setShowProgressDialog(false);
    setHasShownDialog(false);
    // 重置 Hook 中的狀態
    closeDialog();
  }, [closeDialog]);
    
    const handleClearProgressAndStart = useCallback(() => {
      clearProgress();
      handleStartFromBeginning();
    }, [clearProgress, handleStartFromBeginning]);
    
  // 檢查並顯示觀看進度對話框
  useEffect(() => {
    if (shouldShowDialog && currentProgress && !isRestoring && url && !hasShownDialog) {
      // 延遲顯示對話框，讓影片先載入
      const timer = setTimeout(() => {
        setShowProgressDialog(true);
        setHasShownDialog(true); // 標記已經顯示過對話框
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [shouldShowDialog, currentProgress, isRestoring, url, hasShownDialog]);

  // 🔧 修復：組件卸載時強制保存當前進度
  useEffect(() => {
    return () => {
      // 組件卸載時保存當前進度
      if (playerRef.current && currentVideoId && videoDuration > 0) {
        try {
          const currentTime = playerRef.current.getCurrentTime();
          if (currentTime && currentTime > 0) {
            saveProgress(currentTime, videoDuration);
            // 調試日誌已移除
          }
        } catch (error) {
          console.warn('⚠️ 組件卸載時保存進度失敗:', error);
        }
      }
    };
  }, [currentVideoId, videoDuration, saveProgress]);

    // 檢測是否為手機設備
    useEffect(() => {
      const checkMobile = () => {
        // 更寬鬆的手機檢測，包括平板
        const mobile = window.innerWidth <= 800;
        setIsMobile(mobile);
        // 調試日誌已移除
      };
      
      checkMobile();
      window.addEventListener('resize', checkMobile, { passive: true });
      
      return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // 檢測影片尺寸
    const handleVideoReady = (event: any) => {
      // 調試日誌已移除
      onReady?.();
      
      // 嘗試獲取影片尺寸
      try {
        const video = event.target;
        if (video && video.videoWidth && video.videoHeight) {
          const dimensions = {
            width: video.videoWidth,
            height: video.videoHeight
          };
          setVideoDimensions(dimensions);
          
          // 檢測是否為直式影片 (寬度小於高度)
          const vertical = dimensions.width < dimensions.height;
          setIsVerticalVideo(vertical);
          
          // 通知父組件影片方向變化
          onVerticalVideoChange?.(vertical);
          
          // 調試日誌已移除
          // 調試日誌已移除
          
          if (isMobile && vertical) {
            // 調試日誌已移除
          }
        }
      } catch (error) {
        console.log('⚠️ 無法獲取影片尺寸:', error);
      }
    };

    // 強制檢測 YouTube Shorts 為直式影片
    const forceVerticalForShorts = () => {
      if (url && url.includes('/shorts/')) {
        // 調試日誌已移除
        setIsVerticalVideo(true);
        setVideoDimensions({ width: 1080, height: 1920 }); // 預設 Shorts 尺寸
        
        // 通知父組件影片方向變化
        onVerticalVideoChange?.(true);
      }
    };

    // 在 URL 變化時強制檢測 Shorts
    useEffect(() => {
      forceVerticalForShorts();
    }, [url]);

    // 檢查是否為 Google Drive URL
    const isGoogleDriveUrl = (url: string): boolean => {
      return url.includes('drive.google.com') || url.includes('docs.google.com');
    };

    // 檢查是否為 YouTube URL
    const isYouTubeUrl = (url: string): boolean => {
      return url.includes('youtube.com') || url.includes('youtu.be');
    };

    // 提取 YouTube ID
    const extractYouTubeId = (url: string): string | null => {
      const patterns = [
        /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/|youtube\.com\/watch\?.*&v=)([a-zA-Z0-9_-]{11})/,
        /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
        /youtube\.com\/live\/([a-zA-Z0-9_-]{11})/,
        /youtu\.be\/([a-zA-Z0-9_-]{11})/
      ];
      for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match) return match[1];
      }
      return null;
    };

    // 載入 YouTube 字幕
    const loadCaptions = async (videoId: string) => {
      try {
        const cleanId = videoId.trim();
        
        // 檢查是否為有效的 YouTube ID
        if (!cleanId || cleanId.length !== 11) {
          console.log('跳過字幕載入：無效的 YouTube ID');
          return;
        }
        
        // 啟用字幕 API 請求
        console.log('開始載入 YouTube 字幕:', cleanId);
        try {
          const response = await fetch(`/api/youtube-captions/${cleanId}`);
          if (response.ok) {
            const data = await response.json();
            setCaptions(data.captions || []);
            
            // 自動選擇第一個字幕
            if (data.captions && data.captions.length > 0) {
              setSelectedCaption(data.captions[0]);
              setShowCaptionControls(true);
              // 通知父組件字幕已載入
              onCaptionTextChange?.('字幕已載入');
              // 調試日誌已移除
            } else {
              console.log('⚠️ 沒有找到字幕');
              setCaptions([]);
              setSelectedCaption(null);
              setShowCaptionControls(false);
              onCaptionTextChange?.('');
            }
          } else {
            console.log('⚠️ 字幕 API 請求失敗:', response.status);
            setCaptions([]);
            setSelectedCaption(null);
            setShowCaptionControls(false);
            onCaptionTextChange?.('');
          }
        } catch (error) {
          console.error('❌ 載入字幕失敗:', error);
          setCaptions([]);
          setSelectedCaption(null);
          setShowCaptionControls(false);
          onCaptionTextChange?.('');
        }
      } catch (error) {
        console.error('載入字幕失敗:', error);
        onCaptionTextChange?.('');
      }
    };

    // 處理 Google Drive 影片
    useEffect(() => {
      // 暫時禁用 Google Drive 處理，避免無限渲染
      return;
      
      if (!url) return;

      if (isGoogleDriveUrl(url)) {
        const loadGoogleDriveVideo = async () => {
          let retryCount = 0;
          const maxRetries = 3;
          
          const attemptLoad = async (): Promise<void> => {
            try {
              setIsLoading(true);
              setError(null);
              // 調試日誌已移除
              
              // 提取檔案 ID
              const fileId = googleDriveService.extractFileId(url);
              console.log(`[VideoPlayer] 檔案 ID:`, fileId);
              
              // 獲取串流 URL
              const videoStreamUrl = await googleDriveService.getVideoStreamUrl(fileId);
              console.log(`[VideoPlayer] 串流 URL:`, videoStreamUrl);
              
                             setStreamUrl(videoStreamUrl);
               retryCount = 0; // 重置重試次數
            } catch (err) {
              console.error(`[VideoPlayer] 載入 Google Drive 影片失敗 (第 ${retryCount + 1} 次):`, err);
              
              if (retryCount < maxRetries - 1) {
                retryCount++;
                console.log(`[VideoPlayer] ${2} 秒後重試...`);
                setTimeout(attemptLoad, 2000);
                return;
              }
              
              const errorMessage = err instanceof Error ? err.message : '載入影片失敗';
              // 檢查是否為 OAuth 相關錯誤
              if (errorMessage.includes('deleted_client') || errorMessage.includes('OAuth') || errorMessage.includes('401')) {
                setError('Google Drive OAuth 設定有問題。請聯繫開發者更新 OAuth 設定，或使用其他影片來源。');
              } else {
                setError(`載入失敗 (已重試 ${maxRetries} 次): ${errorMessage}`);
              }
              onError?.(err);
            } finally {
              setIsLoading(false);
            }
          };
          
          await attemptLoad();
        };
        loadGoogleDriveVideo();
      } else {
        // 非 Google Drive URL，直接使用
        setStreamUrl(url);
        setError(null);
      }
    }, [url]);

    // 載入 YouTube 字幕（優化版本，減少不必要的請求）
    const prevUrlRef = useRef(url);
    
    useEffect(() => {
      // 暫時禁用字幕功能，避免無限渲染
      return;
      
      // 只有當 URL 真正改變時才執行
      if (prevUrlRef.current !== url) {
        prevUrlRef.current = url;
        
        if (isYouTubeUrl(url)) {
          const videoId = extractYouTubeId(url);
          if (videoId && videoId!.length === 11) {
            // 只在有效的 YouTube ID 時載入字幕
            console.log('載入 YouTube 字幕:', videoId);
            loadCaptions(videoId!);
          } else {
            console.log('跳過字幕載入：無效的 YouTube ID');
            setCaptions([]);
            setSelectedCaption(null);
            setShowCaptionControls(false);
            onCaptionTextChange?.('');
          }
        } else {
          // 非 YouTube 影片，清空字幕
          setCaptions([]);
          setSelectedCaption(null);
          setShowCaptionControls(false);
          onCaptionTextChange?.('');
        }
      }
    }, [url]); // 保留 url 依賴，但用 ref 控制執行

    // 字幕內容更新 - 嘗試獲取 YouTube 實際字幕
    useEffect(() => {
      // 暫時禁用字幕功能，避免無限渲染
      return;
      
      if (!playing || !showCaptionControls) {
        onCaptionTextChange?.('');
        return;
      }

      // 檢查是否為 YouTube 影片
      const isYouTubeVideo = url?.includes('youtube.com') || url?.includes('youtu.be');
      
      if (isYouTubeVideo && currentTime !== undefined) {
        // 只在播放時獲取字幕，不使用 setInterval
        const fetchYouTubeCaptions = async () => {
          try {
            // 從 URL 提取 video ID
            const videoId = url?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/)?.[1];
            
            if (videoId && currentTime !== undefined) {
              // 調用後端 API 獲取字幕
              const apiUrl = `/api/youtube-captions/${videoId}?time=${Math.floor(currentTime)}`;
              
              const response = await fetch(apiUrl);
              
              if (response.ok) {
                const data = await response.json();
                if (data.caption && data.caption.trim()) {
                  onCaptionTextChange?.(data.caption);
                  return;
                }
              } else if (response.status === 403) {
                // 403 錯誤時，直接顯示默認提示，不再重試
                onCaptionTextChange?.('此影片暫無字幕支援');
                return;
              }
            }
          } catch (error) {
            // 靜默處理錯誤，避免無限重試
            console.log('字幕獲取錯誤:', error);
          }
          
          // 如果無法獲取 YouTube 字幕，顯示默認提示
          onCaptionTextChange?.('此影片暫無字幕支援');
        };
        
        // 只在播放時調用一次，不使用 interval
        fetchYouTubeCaptions();
      } else {
        // 非 YouTube 影片或沒有播放時間，顯示默認提示
        onCaptionTextChange?.('此影片暫無字幕支援');
      }
    }, [playing, showCaptionControls, onCaptionTextChange]); // 移除 currentTime 依賴


    // 顯示載入狀態
    if (isLoading) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100%"
          minHeight={200}
          style={style}
        >
          <CircularProgress />
        </Box>
      );
    }

    // 顯示錯誤狀態
    if (error) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100%"
          minHeight={200}
          style={style}
        >
          <Alert severity="error" sx={{ maxWidth: '80%' }}>
            <Typography variant="body2" color="error" align="center">
              {error}
            </Typography>
            {(error.includes('OAuth') || error.includes('401') || error.includes('deleted_client')) && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" sx={{ fontSize: '0.875rem', mb: 1 }}>
                  解決方案：
                </Typography>
                <Typography variant="body2" component="ul" sx={{ fontSize: '0.875rem', pl: 2, m: 0 }}>
                  <li>使用 YouTube Tab 播放 YouTube 影片</li>
                  <li>使用 Video Tab 播放本地影片檔案</li>
                  <li>使用 Short Tab 播放短影片</li>
                  <li>聯繫開發者更新 Google OAuth 設定</li>
                </Typography>
              </Box>
            )}
          </Alert>
        </Box>
      );
    }

    // 沒有 URL 時的狀態
    if (!url) {
      return (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            backgroundImage: 'url(/playbackground.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 200,
            ...style
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <Typography 
              variant="h6" 
              color="white" 
              sx={{ 
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                textAlign: 'center',
                fontWeight: 'bold'
              }}
            >
              請輸入影片網址
            </Typography>
          </Box>
        </Box>
      );
    }

    // 渲染播放器
    // 移除調試日誌，避免無限渲染
    // console.log('🎬 渲染 VideoPlayer:', {
    //   isMobile,
    //   isVerticalVideo,
    //   videoDimensions,
    //   url: url?.substring(0, 50) + '...'
    // });
    
    // 計算手機直式影片的樣式
    const mobileVerticalStyle = isMobile && isVerticalVideo ? {
      width: '100%',
      height: 'auto',
      aspectRatio: '9/16' as const,
      objectFit: 'contain' as const,
      backgroundColor: '#000',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    } : {};
    
    // 移除 console.log，避免無限渲染
    // console.log('📱 手機直式樣式:', mobileVerticalStyle);
    
    return (
      <>
        <Box 
          sx={{ 
            position: 'relative', 
            width: '100%', 
            height: '100%',
          // 手機上直式影片的特殊處理
          ...(isMobile && isVerticalVideo && {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#000',
            // 更強制的 CSS 選擇器，覆蓋 ReactPlayer 的所有內部元素
            '& > div, & > div > div, & > div > div > div, & > div > div > div > div, & > div > div > div > div > div, & > div > div > div > div > div > div': {
              width: '100% !important',
              maxWidth: '100vw !important',
              height: '100% !important', // 改為100%高度填滿容器
              objectFit: 'contain !important', // 改為 contain 保持影片完整顯示
              display: 'flex !important',
              justifyContent: 'center !important',
              alignItems: 'center !important'
            },
            // 針對 YouTube 播放器的特殊處理
            '& iframe': {
              width: '100% !important',
              height: '100% !important', // 改為100%高度填滿容器
              objectFit: 'contain !important', // 改為 contain 保持影片完整顯示
              objectPosition: 'center !important' // 確保影片置中
            }
          })
        }}
      >
        {/* 手機直式影片的強制樣式注入 - 只在真正的手機模式下應用 */}
        {isMobile && isVerticalVideo && (
          <style>
            {`
              /* 只在手機/平板模式下應用直式樣式 */
              @media (max-width: 800px) {
                .mobile-vertical-video,
                .mobile-vertical-video > div,
                .mobile-vertical-video > div > div,
                .mobile-vertical-video > div > div > div,
                .mobile-vertical-video > div > div > div > div,
                .mobile-vertical-video > div > div > div > div > div,
                .mobile-vertical-video > div > div > div > div > div > div {
                  width: 100% !important;
                  max-width: 100vw !important;
                  height: 100% !important; /* 改為100%高度填滿容器 */
                  object-fit: cover !important;
                  display: flex !important;
                  justify-content: center !important;
                  align-items: center !important;
                }

                .mobile-vertical-video iframe {
                  width: 100% !important;
                  height: 100% !important; /* 改為100%高度填滿容器 */
                  object-fit: cover !important;
                  object-position: center !important; /* 確保影片置中 */
                }
              }
            `}
          </style>
        )}
        
        {/* YouTube影片強制填滿樣式 */}
        {url && (url.includes('youtube.com') || url.includes('youtu.be')) && (
          <style>
            {`
              /* 強制YouTube影片填滿容器 */
              .react-player {
                width: 100% !important;
                height: 100% !important;
              }
              .react-player > div {
                width: 100% !important;
                height: 100% !important;
              }
              .react-player iframe {
                width: 100% !important;
                height: 100% !important;
                object-fit: cover !important;
                object-position: center !important;
              }
            `}
          </style>
        )}
        <ReactPlayer
          ref={playerRef}
          url={streamUrl || url}
          playing={playing}
          volume={volume}
          muted={muted}
          playbackRate={playbackRate}
          className={isMobile && isVerticalVideo ? 'mobile-vertical-video' : undefined}
          onProgress={(state: any) => {
            // 🔧 修復：移除頻繁的日誌輸出，只保留必要的回調
            // console.log('VideoPlayer onProgress:', state);
            onProgress?.(state);
            onTimeUpdate?.(state.playedSeconds);
            
            // 🔧 修復：改進觀看進度保存邏輯
            if (currentVideoId && state.playedSeconds && videoDuration > 0) {
              // 每5秒保存一次觀看進度，提高保存頻率
              if (Math.floor(state.playedSeconds) % 5 === 0 && state.playedSeconds > 0) {
                saveProgress(state.playedSeconds, videoDuration);
              }
            }
          }}
          onDuration={(duration: number) => {
            setVideoDuration(duration);
            onDuration?.(duration);
          }}
          onReady={handleVideoReady}
          onLoad={(event: any) => {
            // 調試日誌已移除
            // 對於 YouTube 影片，嘗試從 event 中獲取尺寸
            try {
              const video = event.target;
              if (video && video.videoWidth && video.videoHeight) {
                const dimensions = {
                  width: video.videoWidth,
                  height: video.videoHeight
                };
                setVideoDimensions(dimensions);
                
                // 檢測是否為直式影片 (寬度小於高度)
                const vertical = dimensions.width < dimensions.height;
                setIsVerticalVideo(vertical);
                
                // 通知父組件影片方向變化
                onVerticalVideoChange?.(vertical);
                
                // 調試日誌已移除
                // 調試日誌已移除
                
                if (isMobile && vertical) {
                  // 調試日誌已移除
                }
              }
            } catch (error) {
              console.log('⚠️ 無法獲取影片尺寸 (onLoad):', error);
            }
          }}
          onPlay={() => {
            // 🔧 修復：減少日誌輸出，避免性能問題
            // 調試日誌已移除
            
            // 🔧 新增：防重複機制，避免頻繁觸發
            const now = Date.now();
            if (now - (window as any).lastPlayTime < 2000) { // 2秒內不重複執行
              return;
            }
            (window as any).lastPlayTime = now;
            
            // 每次開始播放時都增加關注度
            let videoIdForAttention = videoId || url;
            
            // 如果是 YouTube 影片，提取 videoId
            if (url.includes('youtube.com') || url.includes('youtu.be')) {
              const extractYouTubeId = (url: string): string | null => {
                const match = url.match(/(?:v=|youtu\.be\/|embed\/)([a-zA-Z0-9_-]{11})/);
                return match ? match[1] : null;
              };
              const extractedId = extractYouTubeId(url);
              if (extractedId) {
                videoIdForAttention = extractedId;
              }
            }
            
            // 調試日誌已移除
            incrementAttention(videoIdForAttention);
            
            // 🔧 修復：只在用戶主動播放時才更新觀看記錄，不在自動載入時更新
            // 檢查是否為用戶主動操作（點擊播放按鈕或鍵盤事件）
            const isUserInitiated = (window as any).__checkplay_user_initiated_play;
            
            // 🔧 修復：使用後立即重置標記，避免影響下一次播放
            if (isUserInitiated) {
              // 調試日誌已移除
              delete (window as any).__checkplay_user_initiated_play;
            }
            
            if (firestoreId && isUserInitiated) {
              // 調試日誌已移除
              const updateWatchHistory = async () => {
                try {
                  // 等待 Firebase 初始化
                  await waitForFirebase();
                  
                  // 獲取當前用戶ID
                  const auth = getAuth();
                  const currentUser = auth?.currentUser;
                  if (!currentUser) {
                    // 調試日誌已移除
                    return;
                  }
                  
                  // 🔧 修復：確保使用正確的 videoId 更新觀看記錄
                  // 如果是社群影片，需要從 firestoreId 中提取正確的 videoId
                  let correctVideoId = videoIdForAttention;
                  if (firestoreId && firestoreId.includes('_')) {
                    // 從複合 ID 中提取 videoId：userId_videoId_version
                    const parts = firestoreId.split('_');
                    if (parts.length >= 3) {
                      correctVideoId = parts[1]; // 第二部分是 videoId
                      // 調試日誌已移除
                    }
                  } else {
                    // 🔧 新增：如果 firestoreId 就是 videoId（來自 OpenPage），直接使用
                    correctVideoId = firestoreId;
                    // 調試日誌已移除
                  }
                  
                  // 🔧 修復：移除自動創建邏輯，因為 OpenPage 已經正確創建了完整格式的文檔
                  // 之前的邏輯會用簡單 videoId 創建文檔，導致重複
                  // OpenPage 使用完整格式: userId_videoId_version
                  // VideoPlayer 使用簡單格式: videoId
                  // → 兩者不匹配，導致創建重複文檔
                  
                  // 使用新的用戶觀看記錄服務
                  const { updateUserWatchHistory } = await import('../services/userWatchHistoryService');
                  
                  // 🔧 修復：確保使用與 sharedVbids 文檔中相同的 videoId
                  // 查詢 sharedVbids 文檔以獲取正確的 videoId
                  try {
                    const db = getDb();
                    if (db && firestoreId) {
                      const sharedVbidsRef = doc(db, 'sharedVbids', firestoreId);
                      const sharedVbidsDoc = await getDoc(sharedVbidsRef);
                      if (sharedVbidsDoc.exists()) {
                        const sharedVbidsData = sharedVbidsDoc.data();
                        if (sharedVbidsData?.videoId) {
                          correctVideoId = sharedVbidsData.videoId;
                          // 調試日誌已移除
                        }
                      }
                    }
                  } catch (error) {
                    console.warn('⚠️ 無法從 sharedVbids 文檔獲取 videoId，使用預設值:', error);
                  }
                  
                  await updateUserWatchHistory(currentUser.uid, correctVideoId, firestoreId);
                  
                  // 調試日誌已移除
                } catch (error) {
                  console.warn('⚠️ sharedVbids 觀看記錄更新失敗:', firestoreId, error);
                  try {
                    // 如果 sharedVbids 失敗，嘗試在 videos 集合中更新
                    const db = getDb();
                    if (!db) {
                      throw new Error('Firestore 未初始化');
                    }
                    const videosRef = doc(db, 'videos', firestoreId);
                    const auth = getAuth();
                    const currentUser = auth?.currentUser;
                    if (currentUser) {
                      // 登入用戶：更新個人觀看記錄 + 影片最後瀏覽時間
                      await updateDoc(videosRef, {
                        lastOpenDate: new Date().toISOString(),
                        lastReadAt: serverTimestamp()
                      });
                      
                      // 使用用戶專屬的觀看記錄服務
                      const { updateUserWatchHistory } = await import('../services/userWatchHistoryService');
                      await updateUserWatchHistory(currentUser.uid, videoIdForAttention, firestoreId);
                    } else {
                      // 訪客：只更新影片最後瀏覽時間，不記錄個人觀看記錄
                      await updateDoc(videosRef, {
                        lastOpenDate: new Date().toISOString()
                      });
                    }
                    // 調試日誌已移除
                  } catch (secondError) {
                    console.warn('⚠️ videos 觀看記錄更新也失敗:', firestoreId, secondError);
                    console.warn('⚠️ 無法更新觀看記錄:', firestoreId, 'sharedVbids 錯誤:', error, 'videos 錯誤:', secondError);
                    
                    // 嘗試查詢文件是否存在
                    try {
                      const db = getDb();
                      if (!db) {
                        throw new Error('Firestore 未初始化');
                      }
                      const docRef = doc(db, 'sharedVbids', firestoreId);
                      const docSnap = await getDoc(docRef);
                      if (docSnap.exists()) {
                        console.log('🔍 文件存在，但更新失敗:', firestoreId);
                      } else {
                        // 調試日誌已移除
                      }
                    } catch (queryError) {
                      console.warn('⚠️ 查詢文件失敗:', firestoreId, queryError);
                    }
                  }
                }
              };
              
              // 非同步執行，不阻塞播放
              updateWatchHistory();
            } else if (firestoreId && !isUserInitiated) {
              // 調試日誌已移除
            } else {
              console.warn('⚠️ firestoreId 為空，無法更新 lastOpenDate');
            }
          }}
          onError={(error) => {
            console.error('VideoPlayer onError:', error);
            onError?.(error);
          }}
          onBuffer={() => {
            // 🔧 修復：移除緩衝日誌，避免過多輸出影響性能
            // console.log('VideoPlayer onBuffer - 開始緩衝');
          }}
          onBufferEnd={() => {
            // 🔧 修復：移除緩衝日誌，避免過多輸出影響性能
            // console.log('VideoPlayer onBufferEnd - 緩衝結束');
          }}
          onSeek={() => {
            console.log('VideoPlayer onSeek - 跳轉中');
          }}
          onPause={() => {
            // 調試日誌已移除
          }}
          controls={true}
          width={width}
          height={height}
          style={{
            ...style,
            ...mobileVerticalStyle,
            // 🔧 修復：YouTube影片使用 contain 而不是 cover，避免裁切
            ...(url && (url.includes('youtube.com') || url.includes('youtu.be')) && {
              width: '100% !important',
              height: '100% !important',
              objectFit: 'contain !important', // 改為 contain，保持影片完整顯示
              objectPosition: 'center !important'
            }),
            // 手機直式影片的強制樣式
            ...(isMobile && isVerticalVideo && {
              width: '100%',
              height: 'auto',
              aspectRatio: '9/16' as const,
              objectFit: 'contain' as const,
              backgroundColor: '#000',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            } as any)
          }}
          config={{
            youtube: {
              playerVars: {
                autoplay: 1,
                controls: 1,
                modestbranding: 1,
                rel: 0,
                showinfo: 1,
                iv_load_policy: 3,
                playsinline: 1,
                // 🔧 修復：添加 origin 參數，確保 YouTube 播放器正常顯示
                origin: window.location.origin,
                // 🔧 關鍵優化：如果有 initialStartTime，直接從該時間開始載入影片
                ...(initialStartTime !== null && initialStartTime > 0 && { start: initialStartTime }),
                // 字幕設定 - 啟用字幕功能
                cc_load_policy: showCaptionControls ? 1 : 0,
                cc_lang_pref: selectedCaption?.language || 'zh'
              }
            },
            file: { 
              attributes: { 
                controlsList: 'nodownload',
                crossOrigin: 'anonymous',
                preload: 'metadata'
              },
              forceVideo: true,
              forceHLS: false,
              forceDASH: false,
              // 針對 Google Drive 串流的特殊設定
              hlsOptions: {
                enableWorker: true,
                debug: false,
                lowLatencyMode: true,
                backBufferLength: 90
              }
            }
          }}
          // 增加重試機制
          progressInterval={1000}
          // progressUpdateInterval={1000} // 已移除，避免 React 警告
          // 針對串流的最佳化設定
          playsinline={true}
          pip={false}
          stopOnUnmount={true}
          light={url ? false : '/playbackground.png'}
        />
        
        {/* 手機直式影片提示 - 暫時隱藏 */}
        {/* {isMobile && isVerticalVideo && (
          <Box
            sx={{
              position: 'absolute',
              top: 10,
              left: 10,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            <span>📱</span>
            直式影片模式 (9:16)
          </Box>
        )} */}
        
        {/* 字幕資訊顯示（改進版本） */}
        {showCaptionControls && captions.length > 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: { xs: 5, sm: 10 },
              right: { xs: 5, sm: 10 },
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              color: 'white',
              padding: { xs: '4px 8px', sm: '6px 10px' },
              borderRadius: { xs: '4px', sm: '6px' },
              fontSize: { xs: '10px', sm: '12px' },
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: { xs: '2px', sm: '4px' },
              zIndex: 1000,
              // 手機模式特殊處理
              '@media (max-width: 600px)': {
                fontSize: '9px',
                padding: '3px 6px',
                gap: '2px'
              }
            }}
          >
            <span style={{ fontSize: '12px' }}>📺</span>
            {selectedCaption?.language === 'zh' ? '中文字幕' : 
             selectedCaption?.language === 'en' ? '英文字幕' : 
             `${selectedCaption?.language || '字幕'} (${selectedCaption?.name || '無'})`}
          </Box>
        )}
        
        {/* 字幕載入中指示器 - 暫時隱藏 */}
        {/* {isYouTubeUrl(url) && !showCaptionControls && (
          <Box
            sx={{
              position: 'absolute',
              top: { xs: 5, sm: 10 },
              right: { xs: 5, sm: 10 },
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              color: 'white',
              padding: { xs: '3px 6px', sm: '4px 8px' },
              borderRadius: { xs: '3px', sm: '4px' },
              fontSize: { xs: '9px', sm: '11px' },
              zIndex: 1000,
              // 手機模式特殊處理
              '@media (max-width: 600px)': {
                fontSize: '8px',
                padding: '2px 5px'
              }
            }}
          >
            📺 載入字幕中...
          </Box>
        )} */}
        </Box>
        
        {/* 觀看進度對話框 */}
        {currentProgress && !disableWatchProgress && (
          <WatchProgressDialog
            open={showProgressDialog}
            onClose={() => setShowProgressDialog(false)}
            videoId={currentVideoId}
            videoTitle={videoTitle && videoTitle.trim() ? videoTitle : `影片 ${currentVideoId}`}
            progress={currentProgress!}
            onRestoreProgress={handleRestoreProgress}
            onClearProgress={handleClearProgressAndStart}
            onStartFromBeginning={handleStartFromBeginning}
          />
        )}
      </>
    );
  }
);

// 🔧 修復：添加組件卸載時的清理邏輯
VideoPlayer.displayName = 'VideoPlayer';

export default VideoPlayer; 